import {
  DailyTasksFilters,
} from "../models/Dashboard/DailyTasks/DailyTasksFilters";
import {
  dailyTasksFamilyLabel,
  DailyTasksStage,
} from "../models/Dashboard/DailyTasks/DailyTasks";
import {
  CommanderActionContext,
  trackCommanderAction,
} from "./commanderTracking";
import { DailyTasksActionId } from "./catalog/dailyTasksActions";

/** GA4 custom parameter string values are truncated around 100 chars. */
const GA_PARAM_STRING_MAX = 100;

/** Human titles for Daily Tasks cards / tiles (DebugView + action_name). */
export const DAILY_TASKS_CARD_LABELS: Record<string, string> = {
  cycle_total: "Cycle total",
  sla_adherence: "On-SLA orders",
  top_drag: "Top drag",
  custom_mix: "Customised mix",
  created_to_logo: "Created → logo",
  created_to_upload: "Created → first upload",
  created_to_last_upload: "Created → last upload",
  upload_to_approval: "Upload → approval",
  approved_to_first_file: "Approved → first file",
  approved_to_last_file: "Approved → last file",
  first_file_to_printer: "First file → printer",
  file_to_printer: "File → printer",
  approved_to_sent: "Approved → sent",
  handoff_to_first_pr: "Handoff → first PR",
  handoff_to_covering_pr: "Handoff → covering PR",
  pr_to_sourcing: "PR → sourcing",
  pr_to_finance: "PR → finance",
  finance_to_po: "Finance → PO",
  po_to_vendor: "PO → vendor email",
  po_to_raw_in: "PO → raw inward",
  raw_in_to_raw_qc: "Raw inward → Raw QC",
  raw_qc_to_print_out: "Raw QC → print outward",
  print_out_to_print_in: "Print outward → print inward",
  print_in_to_print_qc: "Print inward → print QC",
  request_to_project: "Request → project",
  request_to_finance: "Request → finance",
  created_to_variants: "Created → variants",
  created_to_invoice: "Created → invoice",
  activity_logos: "Logos uploaded",
  activity_mockups: "Mockups uploaded",
  activity_products: "Products with upload",
  activity_rework: "Rework",
  activity_rejected: "Rejected",
  activity_approved: "Approved",
  activity_deleted: "Deleted",
  activity_files: "Files uploaded",
  activity_file_products: "Products with file",
  activity_sent_to_printer: "Sent to printer",
  activity_prs: "PRs created",
  activity_pr_products: "Products with PR",
  activity_covering_prs: "Covering PR",
  activity_pos: "POs created",
  activity_po_products: "Products with PO",
  activity_all_pos: "All POs",
  activity_po_emails: "Emailed to vendor",
  activity_po_cancelled: "POs cancelled",
  activity_raw_grns: "Raw inwards",
  activity_raw_full: "Raw full inward",
  activity_raw_qc: "Raw QC closed",
  activity_raw_rejects: "Raw rejected qty",
  activity_print_sends: "Print outwards",
  activity_print_full: "Print full outward",
  activity_print_qty: "Print qty outward",
  activity_print_inwards: "Print inwards",
  activity_print_in_full: "Print full inward",
  activity_print_in_qc: "Print QC closed",
  activity_print_in_rejects: "Print rejected",
  activity_project_requests: "Project requests",
  activity_project_finance: "Project finance",
  activity_project_variants: "Variants covered",
  activity_project_invoices: "Project invoices",
};

const SECTION_LABELS: Record<string, string> = {
  days_ledger: "Days ledger",
  stage_turnaround: "Stage turnaround",
  activity: "Activity",
};

const PRESET_LABELS: Record<string, string> = {
  "7d": "Last 7 days",
  "30d": "Last 30 days",
  "90d": "Last 90 days",
  "6m": "Last 6 months",
  fy: "This financial year",
};

/**
 * Short section keys for DebugView: `task_db_{section}_{action}`.
 * Maps stage tab ids (and merged aliases) to readable short names.
 */
export const DAILY_TASKS_STAGE_SHORT: Record<string, string> = {
  projects: "projects",
  mockup_upload: "mockup",
  mockup_approval: "mockup",
  file_upload: "file",
  cdr_to_printer: "file",
  pr_creation: "pr",
  po_creation: "po",
  raw_inward: "raw",
  raw_inward_qc: "raw",
  print_outward: "print_out",
  print_inward: "print_in",
  print_inward_qc: "print_in",
};

/**
 * Short section key for a Daily Tasks stage tab.
 *
 * @param stage - Stage id (e.g. mockup_upload, pr_creation).
 * @returns Short section slug (e.g. mockup, pr).
 */
export function dailyTasksStageShort(stage?: string | null): string {
  if (!stage) return "tasks";
  return (
    DAILY_TASKS_STAGE_SHORT[stage] ||
    String(stage)
      .replace(/[^a-z0-9_]/gi, "_")
      .slice(0, 10)
  );
}

/**
 * GA-friendly work-tile suffixes (match UI labels, not opaque API ids).
 * e.g. needs_request → no_request ("No request"), needs_pr → no_pr.
 */
export const DAILY_TASKS_TILE_SHORT: Record<string, string> = {
  all_pending: "all",
  // Projects
  needs_request: "no_request",
  awaiting_finance: "await_finance",
  variants_incomplete: "var_incomplete",
  // Mockup
  needs_upload: "needs_upload",
  changes_requested: "rework",
  awaiting_approval: "await_approval",
  // File / CDR
  needs_file: "needs_file",
  needs_send: "await_email",
  // PR
  needs_pr: "no_pr",
  partial_pr: "partial",
  pending_approval: "approval",
  // PO
  needs_po: "no_po",
  partial_po: "partial",
  email_to_vendor: "await_email",
  // Raw
  needs_inward: "await_inward",
  partial_inward: "partial_in",
  needs_qc: "await_qc",
  // Print outward
  needs_outward: "await_outward",
  partial_outward: "partial_out",
  // Print inward
  needs_print_in: "await_inward",
  partial_print_in: "partial_in",
  needs_print_qc: "await_qc",
};

/**
 * Short tile key for DebugView event names.
 *
 * @param tileId - Internal work-tile / work-state id (e.g. needs_request).
 * @returns UI-aligned short slug (e.g. no_request).
 */
export function dailyTasksTileShort(tileId?: string | null): string {
  if (!tileId) return "tile";
  const id = String(tileId).toLowerCase();
  if (DAILY_TASKS_TILE_SHORT[id]) return DAILY_TASKS_TILE_SHORT[id];
  return id.replace(/[^a-z0-9_]/g, "_").slice(0, 14) || "tile";
}

/**
 * Builds `task_db_{section}_{action}` (≤40 chars) for DebugView.
 *
 * @param stage - Current stage tab id.
 * @param action - Action suffix (e.g. view, filters_apply, cycle_total_open).
 * @returns GA4 event name.
 */
function taskDbEvent(stage: string | undefined, action: string): string {
  const section = dailyTasksStageShort(stage);
  const safeAction = String(action || "action")
    .replace(/[^a-z0-9_]/gi, "_")
    .replace(/^_+|_+$/g, "");
  return `task_db_${section}_${safeAction}`.slice(0, 40);
}

/**
 * Caps a description to GA's practical string limit.
 *
 * @param text - Full description.
 * @returns Truncated description.
 */
function clipDescription(text: string): string {
  return text.slice(0, GA_PARAM_STRING_MAX);
}

/**
 * Human label for a clickable Daily Tasks card / metric.
 *
 * @param metricId - Stable metric id from the card.
 * @param override - Optional UI title when it differs from the map.
 * @returns Readable card title.
 */
export function dailyTasksCardLabel(
  metricId?: string | null,
  override?: string | null,
): string {
  if (override && override.trim()) return override.trim();
  if (!metricId) return "card";
  return DAILY_TASKS_CARD_LABELS[metricId] || metricId;
}

/**
 * Human label for which dashboard section the card lives in.
 *
 * @param section - Section key (days_ledger, stage_turnaround, activity).
 * @returns Readable section label.
 */
export function dailyTasksCardSectionLabel(section?: string | null): string {
  if (!section) return "";
  return SECTION_LABELS[section] || section;
}

/**
 * Virtual screen name — each stage tab is a distinct screen in GA.
 *
 * @param stage - Daily Tasks stage id.
 * @returns Screen name for GA params.
 */
export function dailyTasksScreenName(stage?: string | null): string {
  const label = stage
    ? dailyTasksFamilyLabel(stage as DailyTasksStage)
    : "Tasks";
  return `Daily Tasks › ${label}`;
}

/**
 * Slim shared context for every Daily Tasks GA event.
 *
 * @param filters - Current Daily Tasks filters.
 * @returns Context params merged into the GA event.
 */
export function dailyTasksFilterContext(
  filters?: DailyTasksFilters | null,
): CommanderActionContext {
  if (!filters) {
    return { screen_name: "Daily Tasks" };
  }

  const projectStatus =
    filters.project_status === null
      ? "both"
      : filters.project_status
        ? "archived"
        : "active";

  return {
    screen_name: dailyTasksScreenName(filters.stage),
    stage: filters.stage,
    stage_key: dailyTasksStageShort(filters.stage),
    stage_label: dailyTasksFamilyLabel(filters.stage),
    start_date: filters.start_date.format("YYYY-MM-DD"),
    end_date: filters.end_date.format("YYYY-MM-DD"),
    order_type_count: filters.order_type_id?.length ?? 0,
    order_id_count: filters.order_id?.length ?? 0,
    customer_id_count: filters.customer_id?.length ?? 0,
    pm_poc_count: filters.pm_poc_id?.length ?? 0,
    sales_poc_count: filters.sales_poc_id?.length ?? 0,
    project_status: projectStatus,
    include_running: filters.include_running,
    excluded_orders: filters.excluded_orders,
    page_no: filters.page_no,
    page_size: filters.limit,
    sort_by: filters.sort_by,
    sort_type: filters.sort_type,
  };
}

type LabelExtra = {
  previousStage?: string;
  stage?: string;
  preset?: string;
  expanded?: boolean;
  workTile?: string;
  workTileId?: string;
  urgency?: string;
  sortColumn?: string;
  sortDir?: string;
  page?: number;
  pageSize?: number;
  previousPageSize?: number;
  metricId?: string;
  cardTitle?: string;
  cardSection?: string;
  recordScope?: string;
  orderId?: number | string;
  productId?: number | string;
  searchTerm?: string;
};

/**
 * Builds `task_db_{section}_{metric}_{verb}` so DebugView shows section + card.
 * Shortens `activity_*` → `act_*` so names stay ≤40 chars.
 *
 * @param stage - Current stage tab id.
 * @param metricId - Card metric id.
 * @param verb - open | close | scope | sort | page | rows | export | formula.
 * @returns Event name ≤40 chars.
 */
function dailyTasksCardEventName(
  stage: string | undefined,
  metricId: string | undefined,
  verb: string,
): string {
  let id = String(metricId || "card").replace(/[^a-z0-9_]/gi, "_");
  if (id.startsWith("activity_")) {
    id = `act_${id.slice("activity_".length)}`;
  }
  const section = dailyTasksStageShort(stage);
  const prefix = `task_db_${section}_`;
  const maxIdLen = Math.max(4, 40 - prefix.length - 1 - verb.length);
  return `${prefix}${id.slice(0, maxIdLen)}_${verb}`.slice(0, 40);
}

/**
 * Builds dynamic action_name + action_description for Daily Tasks GA.
 *
 * @param actionId - Stable daily tasks action id.
 * @param filters - Filters for brief context.
 * @param extra - Optional action-specific label inputs.
 * @returns Override labels for trackCommanderAction.
 */
export function dailyTasksActionLabels(
  actionId: string,
  filters?: DailyTasksFilters | null,
  extra?: LabelExtra,
): Pick<CommanderActionContext, "action_name" | "action_description"> {
  const stageLabel = dailyTasksFamilyLabel(
    (extra?.stage || filters?.stage || "mockup_upload") as DailyTasksStage,
  );
  const start = filters?.start_date?.format("YYYY-MM-DD") || "";
  const end = filters?.end_date?.format("YYYY-MM-DD") || "";
  const brief = start && end ? `${start}→${end}` : "current filters";
  const card = dailyTasksCardLabel(extra?.metricId, extra?.cardTitle);
  const section = dailyTasksCardSectionLabel(extra?.cardSection);
  const sectionBit = section ? ` · ${section}` : "";

  switch (actionId) {
    case "dashboard_view":
      return {
        action_name: `Opened Daily Tasks › ${stageLabel}`,
        action_description: clipDescription(
          `Loaded Tasks dashboard on ${stageLabel}. ${brief}`,
        ),
      };
    case "filter_apply":
      return {
        action_name: `Applied filters on ${stageLabel}`,
        action_description: clipDescription(
          `Refreshed Daily Tasks › ${stageLabel}. ${brief}`,
        ),
      };
    case "filter_clear":
      return {
        action_name: `Cleared filters on ${stageLabel}`,
        action_description: clipDescription(
          `Reset filters on ${stageLabel} (stage kept)`,
        ),
      };
    case "stage_tab_change": {
      const from = extra?.previousStage
        ? dailyTasksFamilyLabel(extra.previousStage as DailyTasksStage)
        : "previous";
      return {
        action_name: `Switched to ${stageLabel} tab`,
        action_description: clipDescription(
          `Left ${from}, opened ${stageLabel} stage. ${brief}`,
        ),
      };
    }
    case "date_preset_select": {
      const presetId = extra?.preset || "date";
      const presetLabel =
        PRESET_LABELS[presetId] || String(presetId).toUpperCase();
      return {
        action_name: `Selected ${presetLabel}`,
        action_description: clipDescription(
          `Picked date preset ${presetLabel} on ${stageLabel} (not applied yet)`,
        ),
      };
    }
    case "date_range_change":
      return {
        action_name: `Picked custom dates ${brief}`,
        action_description: clipDescription(
          `Changed custom date range to ${brief} on ${stageLabel} (draft)`,
        ),
      };
    case "search_panel_toggle":
      return {
        action_name: `${extra?.expanded ? "Expanded" : "Collapsed"} filters panel`,
        action_description: clipDescription(
          `${extra?.expanded ? "Expanded" : "Collapsed"} Daily Tasks filters panel`,
        ),
      };
    case "filter_order_type_change":
      return {
        action_name: "Changed order type (draft)",
        action_description: clipDescription(
          `Updated order type filter on ${stageLabel} (Apply to refresh)`,
        ),
      };
    case "filter_order_id_change":
      return {
        action_name: "Changed order ID (draft)",
        action_description: clipDescription(
          `Updated order ID filter on ${stageLabel} (Apply to refresh)`,
        ),
      };
    case "filter_customer_change":
      return {
        action_name: "Changed customer (draft)",
        action_description: clipDescription(
          `Updated customer filter on ${stageLabel} (Apply to refresh)`,
        ),
      };
    case "filter_pm_poc_change":
      return {
        action_name: "Changed PM POC (draft)",
        action_description: clipDescription(
          `Updated PM POC filter on ${stageLabel} (Apply to refresh)`,
        ),
      };
    case "filter_sales_poc_change":
      return {
        action_name: "Changed sales POC (draft)",
        action_description: clipDescription(
          `Updated sales POC filter on ${stageLabel} (Apply to refresh)`,
        ),
      };
    case "filter_project_status_change":
      return {
        action_name: "Changed project status (draft)",
        action_description: clipDescription(
          `Updated project status on ${stageLabel} (Apply to refresh)`,
        ),
      };
    case "filter_excluded_orders_change":
      return {
        action_name: "Changed excluded orders (draft)",
        action_description: clipDescription(
          `Toggled excluded-from-sourcing on ${stageLabel} (Apply to refresh)`,
        ),
      };
    case "filter_include_running_change":
      return {
        action_name: "Changed include running (draft)",
        action_description: clipDescription(
          `Toggled include running on ${stageLabel} (Apply to refresh)`,
        ),
      };
    case "filter_date_field_change":
      return {
        action_name: "Changed date field (draft)",
        action_description: clipDescription(
          `Updated date field on ${stageLabel} (Apply to refresh)`,
        ),
      };
    case "work_tile_select":
      return {
        action_name: `Selected work tile: ${extra?.workTile || "tile"}`,
        action_description: clipDescription(
          `Filtered ${stageLabel} queue by "${extra?.workTile}"${
            extra?.urgency && extra.urgency !== "any"
              ? ` · urgency ${extra.urgency}`
              : ""
          }`,
        ),
      };
    case "queue_sort":
      return {
        action_name: `Sorted ${stageLabel} queue by ${extra?.sortColumn} ${extra?.sortDir}`,
        action_description: clipDescription(
          `Sorted ${stageLabel} work queue by ${extra?.sortColumn} ${extra?.sortDir}`,
        ),
      };
    case "queue_page_change":
      return {
        action_name: `${stageLabel} queue page → ${extra?.page}`,
        action_description: clipDescription(
          `Changed ${stageLabel} queue to page ${extra?.page} (size ${extra?.pageSize})`,
        ),
      };
    case "queue_page_size_change":
      return {
        action_name: `${stageLabel} queue rows → ${extra?.pageSize}`,
        action_description: clipDescription(
          `Changed ${stageLabel} queue page size ${extra?.previousPageSize} → ${extra?.pageSize}`,
        ),
      };
    case "drilldown_open":
      return {
        action_name: `Opened card: ${card}`,
        action_description: clipDescription(
          `Opened "${card}"${sectionBit} on ${stageLabel}`,
        ),
      };
    case "drilldown_close":
      return {
        action_name: `Closed card: ${card}`,
        action_description: clipDescription(
          `Closed "${card}"${sectionBit} on ${stageLabel}`,
        ),
      };
    case "drilldown_scope_change":
      return {
        action_name: `${card} scope → ${extra?.recordScope}`,
        action_description: clipDescription(
          `On "${card}": switched scope to ${extra?.recordScope}${sectionBit}`,
        ),
      };
    case "drilldown_sort":
      return {
        action_name: `Sorted ${card} by ${extra?.sortColumn} ${extra?.sortDir}`,
        action_description: clipDescription(
          `On "${card}": sorted by ${extra?.sortColumn} ${extra?.sortDir}${sectionBit}`,
        ),
      };
    case "drilldown_page_change":
      return {
        action_name: `${card} page → ${extra?.page}`,
        action_description: clipDescription(
          `On "${card}": went to page ${extra?.page}${sectionBit}`,
        ),
      };
    case "drilldown_page_size_change":
      return {
        action_name: `${card} rows → ${extra?.pageSize}`,
        action_description: clipDescription(
          `On "${card}": rows per page → ${extra?.pageSize}${sectionBit}`,
        ),
      };
    case "export_csv":
      return {
        action_name: `Exported CSV: ${card}`,
        action_description: clipDescription(
          `Exported CSV for "${card}"${sectionBit} on ${stageLabel}. ${brief}`,
        ),
      };
    case "formula_toggle":
      return {
        action_name: `${extra?.expanded ? "Expanded" : "Collapsed"} formula · ${card}`,
        action_description: clipDescription(
          `${extra?.expanded ? "Expanded" : "Collapsed"} How this is calculated on "${card}"`,
        ),
      };
    case "product_drawer_open":
      return {
        action_name: `Opened product ${extra?.productId} (order ${extra?.orderId})`,
        action_description: clipDescription(
          `Opened product detail for order ${extra?.orderId} · product ${extra?.productId} on ${stageLabel}`,
        ),
      };
    case "product_drawer_close":
      return {
        action_name: "Closed product detail",
        action_description: clipDescription(
          `Closed product detail drawer on ${stageLabel}`,
        ),
      };
    case "link_order_open":
      return {
        action_name: `Opened order ${extra?.orderId}`,
        action_description: clipDescription(
          `Opened project/order ${extra?.orderId} from Daily Tasks › ${stageLabel}`,
        ),
      };
    case "link_product_open":
      return {
        action_name: `Opened product ${extra?.productId}`,
        action_description: clipDescription(
          `Opened product ${extra?.productId} from Daily Tasks › ${stageLabel}`,
        ),
      };
    default:
      return {
        action_name: undefined,
        action_description: clipDescription(
          `Daily Tasks action "${actionId}" on ${stageLabel}. ${brief}`,
        ),
      };
  }
}

type DailyTasksTrackExtra = LabelExtra & {
  context?: CommanderActionContext;
};

/**
 * Preferred helper for Daily Tasks GA.
 * Fires exactly one event per call; never throws into UI/store flows.
 *
 * @param actionId - Catalog action id.
 * @param filters - Current filters for slim context.
 * @param extra - Optional label inputs + raw context fields.
 */
export function trackDailyTasksAction(
  actionId: DailyTasksActionId,
  filters?: DailyTasksFilters | null,
  extra?: DailyTasksTrackExtra,
): void {
  try {
    const { context: extraContext, ...labelExtra } = extra || {};
    const stage = labelExtra.stage || filters?.stage || "mockup_upload";
    const metricId = labelExtra.metricId;
    const cardTitle = dailyTasksCardLabel(metricId, labelExtra.cardTitle);
    const cardSection = labelExtra.cardSection;

    // Pattern: task_db_{section}_{action} — section = projects / mockup / file / pr / …
    let eventName = taskDbEvent(stage, actionId);
    if (actionId === "dashboard_view") {
      eventName = taskDbEvent(stage, "view");
    } else if (actionId === "filter_apply") {
      eventName = taskDbEvent(stage, "filters_apply");
    } else if (actionId === "filter_clear") {
      eventName = taskDbEvent(stage, "filters_clear");
    } else if (actionId === "stage_tab_change") {
      eventName = taskDbEvent(stage, "tab");
    } else if (actionId === "date_preset_select") {
      const presetKey = String(labelExtra.preset || "date")
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");
      eventName = taskDbEvent(stage, `preset_${presetKey || "date"}`);
    } else if (actionId === "date_range_change") {
      eventName = taskDbEvent(stage, "custom_dates");
    } else if (actionId === "search_panel_toggle") {
      eventName = taskDbEvent(stage, "filters_panel");
    } else if (actionId === "work_tile_select") {
      const tileKey = dailyTasksTileShort(
        String(labelExtra.workTileId || labelExtra.workTile || "tile"),
      );
      eventName = taskDbEvent(stage, `tile_${tileKey}`);
    } else if (actionId === "queue_sort") {
      eventName = taskDbEvent(stage, "queue_sort");
    } else if (actionId === "queue_page_change") {
      eventName = taskDbEvent(stage, "queue_page");
    } else if (actionId === "queue_page_size_change") {
      eventName = taskDbEvent(stage, "queue_rows");
    } else if (actionId === "drilldown_open") {
      eventName = dailyTasksCardEventName(stage, metricId, "open");
    } else if (actionId === "drilldown_close") {
      eventName = dailyTasksCardEventName(stage, metricId, "close");
    } else if (actionId === "drilldown_scope_change") {
      eventName = dailyTasksCardEventName(stage, metricId, "scope");
    } else if (actionId === "drilldown_sort") {
      eventName = dailyTasksCardEventName(stage, metricId, "sort");
    } else if (actionId === "drilldown_page_change") {
      eventName = dailyTasksCardEventName(stage, metricId, "page");
    } else if (actionId === "drilldown_page_size_change") {
      eventName = dailyTasksCardEventName(stage, metricId, "rows");
    } else if (actionId === "export_csv") {
      eventName = dailyTasksCardEventName(stage, metricId, "export");
    } else if (actionId === "formula_toggle") {
      eventName = dailyTasksCardEventName(stage, metricId, "formula");
    } else if (actionId === "product_drawer_open") {
      eventName = taskDbEvent(stage, "product_open");
    } else if (actionId === "product_drawer_close") {
      eventName = taskDbEvent(stage, "product_close");
    } else if (actionId === "link_order_open") {
      eventName = taskDbEvent(stage, "link_order");
    } else if (actionId === "link_product_open") {
      eventName = taskDbEvent(stage, "link_product");
    } else if (actionId.startsWith("filter_")) {
      const short = actionId.replace("filter_", "").replace("_change", "");
      eventName = taskDbEvent(stage, `f_${short}`);
    }

    trackCommanderAction({
      moduleId: "daily_tasks",
      actionId,
      eventName,
      context: {
        ...dailyTasksFilterContext(filters),
        ...dailyTasksActionLabels(actionId, filters, {
          ...labelExtra,
          stage,
        }),
        ...(metricId
          ? {
              metric_id: metricId,
              card_title: cardTitle,
              ...(cardSection ? { card_section: cardSection } : {}),
            }
          : {}),
        ...(extraContext || {}),
      },
    });
  } catch {
    // never block UI
  }
}
